import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { CreditCard, Gift, Loader2, Check, X, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminAffiliatesTab = ({ token }) => {
  const [affiliates, setAffiliates] = useState([]);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [affiliateLoading, setAffiliateLoading] = useState(false);

  useEffect(() => { fetchAffiliates(); }, []);

  const fetchAffiliates = async () => {
    setAffiliateLoading(true);
    try {
      const [affRes, payRes] = await Promise.all([
        fetch(`${API_URL}/api/affiliate/admin/affiliates`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/affiliate/admin/payouts/pending`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (affRes.ok) { const d = await affRes.json(); setAffiliates(d.affiliates || []); }
      if (payRes.ok) { const d = await payRes.json(); setPendingPayouts(d.payouts || []); }
    } catch (err) { console.error('Error fetching affiliate data:', err); }
    finally { setAffiliateLoading(false); }
  };

  const processPayoutAction = async (payoutId, action) => {
    try {
      const res = await fetch(`${API_URL}/api/affiliate/admin/payouts/${payoutId}/${action}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(`Payout ${action === 'approve' ? 'approved' : action === 'paid' ? 'marked as paid' : 'rejected'}`);
        fetchAffiliates();
      } else toast.error('Failed to process payout');
    } catch { toast.error('Error processing payout'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-affiliates-tab">
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-amber-400" /> Pending Payout Requests
            {pendingPayouts.length > 0 && <Badge className="bg-amber-500 text-white">{pendingPayouts.length}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {affiliateLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-emerald-400" /></div>
          ) : pendingPayouts.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No pending payout requests</p>
          ) : (
            <div className="space-y-3">
              {pendingPayouts.map(payout => (
                <div key={payout.id} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{payout.affiliate_username}</p>
                    <p className="text-xs text-slate-400">{payout.affiliate_email}</p>
                    <p className="text-xs text-slate-500">Method: {payout.payment_method}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-emerald-400">${payout.amount.toFixed(2)}</p>
                    <p className="text-xs text-slate-400">{new Date(payout.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500" onClick={() => processPayoutAction(payout.id, 'approve')}><Check className="h-4 w-4" /></Button>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-500" onClick={() => processPayoutAction(payout.id, 'paid')}><DollarSign className="h-4 w-4" /></Button>
                    <Button size="sm" variant="destructive" onClick={() => processPayoutAction(payout.id, 'reject')}><X className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Gift className="h-5 w-5 text-purple-400" /> All Affiliates
          </CardTitle>
        </CardHeader>
        <CardContent>
          {affiliateLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-emerald-400" /></div>
          ) : affiliates.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No affiliates with referrals yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-slate-400 text-sm border-b border-slate-800">
                    <th className="py-3 px-2">Affiliate</th>
                    <th className="py-3 px-2">Code</th>
                    <th className="py-3 px-2">Referrals</th>
                    <th className="py-3 px-2">Conversions</th>
                    <th className="py-3 px-2">Earned</th>
                    <th className="py-3 px-2">Pending</th>
                    <th className="py-3 px-2">Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {affiliates.map(a => (
                    <tr key={a.user_id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                      <td className="py-3 px-2"><p className="text-white font-medium">{a.username}</p><p className="text-xs text-slate-400">{a.email}</p></td>
                      <td className="py-3 px-2"><code className="text-emerald-400 bg-slate-800 px-2 py-1 rounded text-xs">{a.referral_code}</code></td>
                      <td className="py-3 px-2 text-white">{a.total_referrals}</td>
                      <td className="py-3 px-2 text-emerald-400">{a.pro_conversions}</td>
                      <td className="py-3 px-2 text-white">${a.total_earned.toFixed(2)}</td>
                      <td className="py-3 px-2 text-amber-400">${a.pending_payout.toFixed(2)}</td>
                      <td className="py-3 px-2 text-slate-400">${a.paid_out.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
